import { useState, useEffect } from "react";
import { C, S, G } from "../../utils/theme";
import { saveUser } from "../../utils/data";

// If the URL is /:slug/student (or /:slug/student-login), pull the slug out
// so the login page can show that university's branding. Purely cosmetic —
// login itself is unaffected.
function slugFromStudentUrl() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  return (parts.length === 2 && (parts[1] === "student" || parts[1] === "student-login" || parts[1] === "studentlogin")) ? parts[0] : null;
}

function AuthInput({ label, ...props }) {
  return (
    <div style={S.formGroup}>
      <label style={S.label}>{label}</label>
      <input style={S.input} {...props} onFocus={e=>e.target.style.borderColor=C.navy} onBlur={e=>e.target.style.borderColor=C.border} />
    </div>
  );
}

function LoginPage({ role, onLogin }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [brand, setBrand] = useState(null); // { name, logo_url } from /:slug/student, else null

  const isAdmin = role === "admin";
  const color = isAdmin ? C.navy : "#8B5CF6";
  const grd = isAdmin ? G.blue : G.purple;

  useEffect(() => {
    if (isAdmin) return;
    const slug = slugFromStudentUrl();
    if (!slug) return;
    import("../../utils/api").then(({ universitiesAPI }) =>
      universitiesAPI.getBySlug(slug).then(res => setBrand(res.data)).catch(() => {})
    );
  }, [isAdmin]);

 const login = async () => {
  setErr("");
  if (!email || !pass) { setErr("Please fill all fields."); return; }
  setLoading(true);
  try {
    const { authAPI } = await import("../../utils/api");
    const res = await authAPI.login(email, pass, role);
    onLogin(res.user, res.token);
  } catch (err) {
    setErr(err.message || "Invalid credentials");
  } finally {
    setLoading(false);
  }
};

  return (
    <div style={S.authWrap}>
      <div style={{ width:"100%", maxWidth:460 }}>
        <div style={{ ...S.authCard, borderTop:`3px solid ${color}` }}>
          <div style={{ textAlign:"center", marginBottom:32 }}>
            <div style={{ width:72, height:72, background:grd, borderRadius:20, display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, margin:"0 auto 16px", boxShadow:`0 6px 16px ${color}33`, overflow:"hidden" }}>
             {!isAdmin && brand?.logo_url
               ? <img src={brand.logo_url} alt={brand.name} style={{ width:"100%", height:"100%", objectFit:"contain", padding:8 }} />
               : <span>{isAdmin ? "👨‍💼" : "🎓"}</span>}
            </div>
            <h1 style={{ fontSize:24, fontWeight:800, color:C.text, margin:0 }}>{isAdmin?"Admin Portal":"Student Portal"}</h1>
            {!isAdmin && <p style={{ fontSize:13, color:C.textMuted, marginTop:6 }}>{brand?.name || "Loading..."}</p>}
          </div>

          {err && <div style={{ background:C.roseLight, color:"#E11D48", padding:"11px 15px", borderRadius:10, fontSize:13, marginBottom:18, border:"1px solid #FCA5B1" }}>⚠️ {err}</div>}

          <div style={S.formGroup}>
            <label style={S.label}>{isAdmin?"Admin Email":"Student Email"}</label>
            <input style={S.input} type="email" placeholder={isAdmin?"Admin Email":"student@uni.edu"} value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} onFocus={e=>e.target.style.borderColor=color} onBlur={e=>e.target.style.borderColor=C.border} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Password</label>
            <div style={{ position:"relative" }}>
              <input style={{ ...S.input, paddingRight:44 }} type={showPass?"text":"password"} placeholder="Enter password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} onFocus={e=>e.target.style.borderColor=color} onBlur={e=>e.target.style.borderColor=C.border} />
              <button type="button" onClick={()=>setShowPass(v=>!v)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:16, color:C.textMuted }}>{showPass?"🙈":"👁️"}</button>
            </div>
          </div>

          <button onClick={login} disabled={loading} style={{ ...S.btn("primary","lg"), width:"100%", justifyContent:"center", background:grd, boxShadow:`0 4px 12px ${color}33`, opacity:loading?0.7:1 }}>
            {loading ? "Signing in..." : `Sign In →`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminLogin({ onLogin }) { return <LoginPage role="admin" onLogin={onLogin} />; }
export function StudentLogin({ onLogin }) { return <LoginPage role="student" onLogin={onLogin} />; }
